import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import IndustryChips from 'components/chip/IndustryChips';
import ProjectStatusChip from 'components/chip/ProjectStatusChip';
import TechStackPreview from 'components/chip/TechStackPreview';
import DetailMetaHeader from 'components/condition-list/DetailMetaHeader';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import ReadOnlyField from 'components/form/ReadOnlyField';
import TextArea from 'components/form/TextArea';
import TechStackSelect from 'components/tech-stack-select/TechStackSelect';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import {
  MasterTypeResult,
  getDistribution,
  getIndustry,
  getProjectScale,
} from 'stores/MasterTypeStoreAdapter';
import {
  ProjectDetailResult,
  ProjectStatus,
  TechStackResult,
  getTechStack,
  updateProject,
} from 'stores/ProjectStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type ProjectUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type ProjectUpdateFormProps = {
  project: ProjectDetailResult;
  isEditing: boolean;
  onProjectUpdated: () => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const ProjectUpdateForm = React.forwardRef<ProjectUpdateFormHandle, ProjectUpdateFormProps>(
  (
    {
      project,
      isEditing,
      onProjectUpdated,
      onClose,
      onEditingChange,
      setProgress,
      hideHeader = false,
    },
    ref,
  ) => {
    const [projectName, setProjectName] = useState(project.projectName);
    const [projectNameKana, setProjectNameKana] = useState(project.projectNameKana);
    const [status, setStatus] = useState<ProjectStatus>(project.status);
    const [projectScaleId, setProjectScaleId] = useState(project.projectScaleId || '');
    const [distributionId, setDistributionId] = useState(project.distributionId || '');
    const [industryIds, setIndustryIds] = useState<string[]>(
      project.industries?.map((i) => i.industryId) || [],
    );
    const [techStackIds, setTechStackIds] = useState<string[]>(() => {
      if (!project.techStacks) return [];
      const ids = new Set(project.techStacks.map((t) => t.techStackId));
      return Array.from(ids);
    });
    const [startDate, setStartDate] = useState(project.startDate || '');
    const [endDate, setEndDate] = useState(project.endDate || '');
    const [remarks, setRemarks] = useState(project.remarks || '');

    const [projectScales, setProjectScales] = useState<MasterTypeResult[]>([]);
    const [distributions, setDistributions] = useState<MasterTypeResult[]>([]);
    const [industries, setIndustries] = useState<MasterTypeResult[]>([]);
    const [techStackData, setTechStackData] = useState<TechStackResult | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [copyTooltip, setCopyTooltip] = useState('Copy to clipboard');

    const isClosed = project.status === 'CLOSED';

    useEffect(() => {
      Promise.all([getProjectScale(), getDistribution(), getIndustry(), getTechStack()])
        .then(([scaleList, distList, indList, techData]) => {
          setProjectScales(scaleList);
          setDistributions(distList);
          setIndustries(indList);
          setTechStackData(techData);
        })
        .catch(() => errorDefault('マスタデータの取得に失敗しました'));
    }, []);

    useEffect(() => {
      setProjectName(project.projectName);
      setProjectNameKana(project.projectNameKana);
      setStatus(project.status);
      setProjectScaleId(project.projectScaleId || '');
      setDistributionId(project.distributionId || '');
      setIndustryIds(project.industries?.map((i) => i.industryId) || []);
      const ids = new Set(project.techStacks?.map((t) => t.techStackId) || []);
      setTechStackIds(Array.from(ids));
      setStartDate(project.startDate || '');
      setEndDate(project.endDate || '');
      setRemarks(project.remarks || '');
      setErrors({});
      onEditingChange(false);
    }, [project]);

    const handleCopyId = () => {
      navigator.clipboard.writeText(project.projectId).then(() => {
        setCopyTooltip('Copied!');
        setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
      });
    };

    const handleBackToReadOnly = () => {
      setProjectName(project.projectName);
      setProjectNameKana(project.projectNameKana);
      setStatus(project.status);
      setProjectScaleId(project.projectScaleId || '');
      setDistributionId(project.distributionId || '');
      setIndustryIds(project.industries?.map((i) => i.industryId) || []);
      const ids = new Set(project.techStacks?.map((t) => t.techStackId) || []);
      setTechStackIds(Array.from(ids));
      setStartDate(project.startDate || '');
      setEndDate(project.endDate || '');
      setRemarks(project.remarks || '');
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!projectName) newErrors.projectName = MSG_REQUIRED('プロジェクト名');
      else if (projectName.length > 300)
        newErrors.projectName = MSG_MAX_LENGTH('プロジェクト名', 300);

      if (!projectNameKana) newErrors.projectNameKana = MSG_REQUIRED('プロジェクト名（カナ）');
      else if (projectNameKana.length > 300)
        newErrors.projectNameKana = MSG_MAX_LENGTH('プロジェクト名（カナ）', 300);

      // ステータス遷移のバリデーション
      if (status === 'ACTIVE' && !startDate) {
        newErrors.startDate = MSG_REQUIRED('開始日');
      }
      if (status === 'CLOSED' && project.status !== 'CLOSED' && !endDate) {
        newErrors.endDate = MSG_REQUIRED('終了日');
      }

      if (startDate && !DATETIME_REGEX.test(startDate))
        newErrors.startDate = MSG_INVALID_DATETIME_FORMAT('開始日');

      if (endDate && !DATETIME_REGEX.test(endDate))
        newErrors.endDate = MSG_INVALID_DATETIME_FORMAT('終了日');

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateProject({
        projectId: project.projectId,
        projectName,
        projectNameKana,
        status,
        ...(projectScaleId && { projectScaleId }),
        ...(distributionId && { distributionId }),
        industryIds: industryIds.length > 0 ? industryIds : undefined,
        techStackIds: techStackIds.length > 0 ? techStackIds : undefined,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('プロジェクト情報を更新しました');
          onProjectUpdated();
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('プロジェクト情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleSave, isEditing: () => isEditing }));

    // 開始日の有効/無効
    const isStartDateDisabled = (): boolean => {
      if (isClosed) return true;
      if (status === 'ACTIVE') return false;
      if (status === 'CLOSED' && project.status !== 'CLOSED') return false;
      return true;
    };

    // 終了日の有効/無効
    const isEndDateDisabled = (): boolean => {
      if (isClosed) return true;
      if (status === 'CLOSED' && project.status !== 'CLOSED') return false;
      return true;
    };

    const getScaleLabel = (id: string): string => {
      const found = projectScales.find((s) => s.typeCode === id);
      return found ? found.typeLabel : id || '-';
    };

    const getDistributionLabel = (id: string): string => {
      const found = distributions.find((d) => d.typeCode === id);
      return found ? found.typeLabel : id || '-';
    };

    const getTechStackLayerGroups = (): { group: string; names: string[] }[] => {
      if (!project.techStacks || project.techStacks.length === 0) return [];
      const grouped: Record<string, string[]> = {};
      project.techStacks
        .filter((t) => t.categoryType === 'TECH_LAYER')
        .forEach((t) => {
          if (!grouped[t.categoryLabel]) grouped[t.categoryLabel] = [];
          if (!grouped[t.categoryLabel].includes(t.techStackName)) {
            grouped[t.categoryLabel].push(t.techStackName);
          }
        });
      return Object.entries(grouped).map(([group, names]) => ({ group, names }));
    };

    const getTechStackTypeGroups = (): { group: string; names: string[] }[] => {
      if (!project.techStacks || project.techStacks.length === 0) return [];
      const grouped: Record<string, string[]> = {};
      project.techStacks
        .filter((t) => t.categoryType === 'TECH_TYPE')
        .forEach((t) => {
          if (!grouped[t.categoryLabel]) grouped[t.categoryLabel] = [];
          if (!grouped[t.categoryLabel].includes(t.techStackName)) {
            grouped[t.categoryLabel].push(t.techStackName);
          }
        });
      return Object.entries(grouped).map(([group, names]) => ({ group, names }));
    };

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            <ProjectStatusChip status={project.status} />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {project.projectId}
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            {subtitle}
          </Typography>
          <Stack direction="column" spacing={0.25} alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">
              作成者: {project.createdByName} 作成日: {formatDate(project.createdAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              更新者: {project.updatedByName} 更新日: {formatDate(project.updatedAt)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!hideHeader && renderHeader('プロジェクト詳細', 'プロジェクト情報を確認します', onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
            {hideHeader && (
              <DetailMetaHeader
                id={project.projectId}
                metaItems={[
                  {
                    label: '作成者',
                    value: `${project.createdByName} (${formatDate(project.createdAt)})`,
                  },
                  {
                    label: '更新者',
                    value: `${project.updatedByName} (${formatDate(project.updatedAt)})`,
                  },
                ]}
              />
            )}
            {/* 基本情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                基本情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="プロジェクト名" value={project.projectName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="プロジェクト名（カナ）" value={project.projectNameKana} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="課長" value={project.manager.userName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="取引先" value={project.client.clientName} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 補足情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                補足情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="規模" value={getScaleLabel(project.projectScaleId || '')} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="商流"
                    value={getDistributionLabel(project.distributionId || '')}
                  />
                </Grid>
                <Grid size={12}>
                  <IndustryChips industries={project.industries || []} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 技術スタック */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                技術スタック
              </Typography>
              <TechStackPreview
                layerGroups={getTechStackLayerGroups()}
                typeGroups={getTechStackTypeGroups()}
              />
            </Box>

            <Divider />

            {/* 期間 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                期間
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField
                    label="開始日"
                    value={project.startDate ? formatDate(project.startDate) : ''}
                  />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField
                    label="終了日"
                    value={project.endDate ? formatDate(project.endDate) : ''}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 備考 */}
            <ReadOnlyField label="備考" value={project.remarks || ''} />
          </Stack>

          {/* 固定フッター: 編集ボタン（CLOSEDでなければ表示） — 外部フッターで管理 */}
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader &&
          renderHeader(
            'プロジェクト詳細（編集中）',
            'プロジェクト情報を編集します',
            handleBackToReadOnly,
          )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {hideHeader && (
            <DetailMetaHeader
              id={project.projectId}
              metaItems={[
                {
                  label: '作成者',
                  value: `${project.createdByName} (${formatDate(project.createdAt)})`,
                },
                {
                  label: '更新者',
                  value: `${project.updatedByName} (${formatDate(project.updatedAt)})`,
                },
              ]}
            />
          )}
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="プロジェクト名"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={isClosed}
                  error={!!errors.projectName}
                  helperText={errors.projectName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="プロジェクト名（カナ）"
                  value={projectNameKana}
                  onChange={(v) => setProjectNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  disabled={isClosed}
                  error={!!errors.projectNameKana}
                  helperText={errors.projectNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField label="課長" value={project.manager.userName} />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField label="取引先" value={project.client.clientName} />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 補足情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              補足情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl disabled={isClosed}>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>規模</FormLabel>
                  <RadioGroup
                    row
                    value={projectScaleId}
                    onChange={(e) => setProjectScaleId(e.target.value)}
                  >
                    {projectScales.map((s) => (
                      <FormControlLabel
                        key={s.typeCode}
                        value={s.typeCode}
                        control={<Radio size="small" />}
                        label={s.typeLabel}
                        disabled={isClosed}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl disabled={isClosed}>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>商流</FormLabel>
                  <RadioGroup
                    row
                    value={distributionId}
                    onChange={(e) => setDistributionId(e.target.value)}
                  >
                    {distributions.map((d) => (
                      <FormControlLabel
                        key={d.typeCode}
                        value={d.typeCode}
                        control={<Radio size="small" />}
                        label={d.typeLabel}
                        disabled={isClosed}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth size="small" disabled={isClosed}>
                  <InputLabel>業界</InputLabel>
                  <Select
                    multiple
                    value={industryIds}
                    label="業界"
                    onChange={(e) => {
                      const value = e.target.value;
                      setIndustryIds(typeof value === 'string' ? value.split(',') : value);
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                    renderValue={(selected) => {
                      const items = selected
                        .map((id) => {
                          const item = industries.find((i) => i.typeCode === id);
                          return item ? { id, name: item.typeLabel } : null;
                        })
                        .filter((item): item is { id: string; name: string } => !!item);
                      return (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {items.map(({ id, name }) => (
                            <Chip
                              key={id}
                              label={name}
                              size="small"
                              {...(!isClosed && {
                                onDelete: (e: React.SyntheticEvent) => {
                                  e.stopPropagation();
                                  setIndustryIds(industryIds.filter((sid) => sid !== id));
                                },
                              })}
                              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                            />
                          ))}
                        </Stack>
                      );
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {industries.map((option) => (
                      <MenuItem key={option.typeCode} value={option.typeCode}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={industryIds.includes(option.typeCode)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {option.typeLabel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 技術スタック */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              技術スタック
            </Typography>
            <TechStackSelect
              techStackData={techStackData}
              selectedIds={techStackIds}
              onChange={setTechStackIds}
              disabled={isClosed}
            />
          </Box>

          <Divider />

          {/* 期間 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              期間
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={startDate}
                  onChange={setStartDate}
                  label="開始日"
                  disabled={isStartDateDisabled()}
                  required={status === 'ACTIVE'}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value={endDate}
                  onChange={setEndDate}
                  label="終了日"
                  disabled={isEndDateDisabled()}
                  required={status === 'CLOSED' && project.status !== 'CLOSED'}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            disabled={isClosed}
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

ProjectUpdateForm.displayName = 'ProjectUpdateForm';

export default ProjectUpdateForm;
